import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { AdminLayout } from "../components/AdminLayout";
import { Button } from "../components/ui/button";
import { DatePickerUS } from "../components/ui/date-picker-us";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { History, Trash2, Lock, MessageCircle, MessageSquare, Bot, Calendar, Receipt, Filter, FileText } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "../i18n/I18nContext";
import { logError } from "../lib/logger";

const EVENT_INFO = {
  confirmation: { labelKey: "Confirmação", icon: MessageCircle, classes: "bg-blue-50 text-blue-700 border-blue-200" },
  reminder: { labelKey: "Lembrete", icon: Bot, classes: "bg-amber-50 text-amber-700 border-amber-200" },
  started: { labelKey: "Equipe chegou", icon: MessageCircle, classes: "bg-sky-50 text-sky-700 border-sky-200" },
  finished: { labelKey: "Concluída", icon: MessageCircle, classes: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  payment_receipt: { labelKey: "Recibo de pagamento", icon: Receipt, classes: "bg-purple-50 text-purple-700 border-purple-200" },
  invoice: { labelKey: "Invoice (Fatura)", icon: FileText, classes: "bg-orange-50 text-orange-700 border-orange-200" },
};

const CHANNEL_INFO = {
  whatsapp: { icon: MessageCircle, labelKey: "WhatsApp", color: "emerald" },
  sms: { icon: MessageSquare, labelKey: "SMS", color: "blue" },
  auto: { icon: Bot, labelKey: "Cron (pendente)", color: "amber" },
};

function defaultRange(kind) {
  const now = new Date();
  const fmt = (d) => d.toISOString().slice(0, 10);
  if (kind === "week") {
    const d = new Date(now);
    d.setDate(d.getDate() - 7);
    return { from: fmt(d), to: fmt(now) };
  }
  if (kind === "month") {
    const d = new Date(now.getFullYear(), now.getMonth(), 1);
    return { from: fmt(d), to: fmt(now) };
  }
  if (kind === "year") {
    return { from: `${now.getFullYear()}-01-01`, to: fmt(now) };
  }
  return { from: "", to: "" };
}

export default function NotificationHistory() {
  const { t } = useI18n();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteId, setDeleteId] = useState(null);
  const [confirmClearOpen, setConfirmClearOpen] = useState(false);
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [range, setRange] = useState({ from: "", to: "" });
  const [typeFilter, setTypeFilter] = useState("all");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (range.from) params.set("from", range.from);
      if (range.to) params.set("to", range.to);
      const qs = params.toString();
      const { data } = await api.get(`/notification-history${qs ? `?${qs}` : ""}`);
      setItems(data);
    } catch (e) {
      logError(e);
    } finally {
      setLoading(false);
    }
  }, [range.from, range.to]);

  useEffect(() => {
    load();
  }, [load]);

  const filteredItems = useMemo(() => {
    if (typeFilter === "all") return items;
    return items.filter((n) => n.event_key === typeFilter);
  }, [items, typeFilter]);

  async function deleteOne() {
    setBusy(true);
    try {
      await api.delete(`/notification-history/${deleteId}`, { data: { password } });
      toast.success(t("Notificação excluída"));
      setDeleteId(null);
      setPassword("");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || t("Erro"));
    } finally {
      setBusy(false);
    }
  }

  async function clearAll() {
    setBusy(true);
    try {
      const { data } = await api.post("/notification-history/clear", { password });
      toast.success(`${data.deleted} ${t("notificações apagadas")}`);
      setConfirmClearOpen(false);
      setPassword("");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || t("Erro"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <AdminLayout>
      <div className="flex flex-wrap items-start justify-between gap-3 mb-6">
        <div>
          <div className="text-xs uppercase tracking-widest text-blue-700 font-semibold">
            {t("Comunicação")}
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-2">
            <History size={26} /> {t("Histórico de notificações")}
          </h1>
          <div className="text-sm text-slate-500 mt-1">
            {t("Registro de mensagens enviadas (manual via WhatsApp/SMS) e lembretes automáticos pendentes (gerados pelo cron a cada 30 min).")}
          </div>
        </div>
        <Button
          onClick={() => setConfirmClearOpen(true)}
          disabled={items.length === 0}
          data-testid="clear-all-btn"
          className="bg-red-600 hover:bg-red-700 text-white"
        >
          <Trash2 size={16} className="mr-1" /> {t("Apagar tudo")}
        </Button>
      </div>

      <div className="cm-card p-3 mb-4 flex flex-wrap items-center gap-2">
        <Calendar size={14} className="text-slate-400" />
        <span className="text-xs text-slate-500 font-semibold mr-1">{t("Período:")}</span>
        {[
          { k: "all", labelKey: "Todos" },
          { k: "week", labelKey: "Última semana" },
          { k: "month", labelKey: "Este mês" },
          { k: "year", labelKey: "Este ano" },
        ].map((opt) => (
          <button
            key={opt.k}
            data-testid={`notif-range-${opt.k}`}
            onClick={() => setRange(defaultRange(opt.k))}
            className="px-3 py-1.5 rounded-full text-xs font-semibold border bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
          >
            {t(opt.labelKey)}
          </button>
        ))}
        <DatePickerUS
          value={range.from}
          onChange={(v) => setRange({ ...range, from: v })}
          testId="notif-range-from"
        />
        <span className="text-slate-400">→</span>
        <DatePickerUS
          value={range.to}
          onChange={(v) => setRange({ ...range, to: v })}
          testId="notif-range-to"
        />
        <span className="text-xs text-slate-500 ml-auto">
          {filteredItems.length} {t("de")} {items.length} {items.length === 1 ? t("registro") : t("registros")}
        </span>
      </div>

      <div className="cm-card p-3 mb-4 flex flex-wrap items-center gap-2">
        <Filter size={14} className="text-slate-400" />
        <span className="text-xs text-slate-500 font-semibold mr-1">{t("Tipo:")}</span>
        {[
          { k: "all", labelKey: "Todos" },
          { k: "confirmation", labelKey: "Confirmação" },
          { k: "reminder", labelKey: "Lembrete" },
          { k: "started", labelKey: "Equipe chegou" },
          { k: "finished", labelKey: "Concluída" },
          { k: "payment_receipt", labelKey: "Recibo de pagamento" },
          { k: "invoice", labelKey: "Invoice" },
        ].map((opt) => {
          const active = typeFilter === opt.k;
          return (
            <button
              key={opt.k}
              data-testid={`notif-type-${opt.k}`}
              onClick={() => setTypeFilter(opt.k)}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                active
                  ? "bg-slate-900 text-white border-slate-900"
                  : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
              }`}
            >
              {t(opt.labelKey)}
            </button>
          );
        })}
      </div>

      <div className="cm-card overflow-hidden">
        {loading ? (
          <div className="text-center py-12 text-slate-400">{t("Carregando…")}</div>
        ) : filteredItems.length === 0 ? (
          <div className="text-center py-16 text-slate-400">
            {items.length === 0
              ? t("Nenhuma notificação enviada ainda.")
              : t("Nenhum registro neste tipo. Tente outro filtro.")}
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-500">
              <tr>
                <th className="text-left p-3">{t("Data")}</th>
                <th className="text-left p-3">{t("Destinatário")}</th>
                <th className="text-left p-3">{t("Tipo")}</th>
                <th className="text-left p-3">{t("Canal")}</th>
                <th className="text-left p-3">{t("Enviado por")}</th>
                <th className="text-left p-3">{t("Mensagem")}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {filteredItems.map((n) => {
                const ch = CHANNEL_INFO[n.channel] || CHANNEL_INFO.sms;
                const Icon = ch.icon;
                return (
                  <tr
                    key={n.id}
                    data-testid={`notif-row-${n.id}`}
                    className="border-b border-slate-100 hover:bg-slate-50"
                  >
                    <td className="p-3 text-xs text-slate-600 whitespace-nowrap">
                      {new Date(n.created_at).toLocaleString("en-US")}
                    </td>
                    <td className="p-3 font-medium">
                      {n.client_name || n.client_id?.slice(0, 8)}
                      {n.client_phone && (
                        <div className="text-xs text-slate-500">
                          {n.client_phone}
                        </div>
                      )}
                    </td>
                    <td className="p-3">
                      {(() => {
                        const ev = EVENT_INFO[n.event_key] || {
                          labelKey: n.event_key,
                          icon: MessageCircle,
                          classes: "bg-slate-100 text-slate-700 border-slate-200",
                        };
                        const Evi = ev.icon;
                        return (
                          <span
                            data-testid={`notif-event-${n.event_key}`}
                            className={`text-xs inline-flex items-center gap-1 px-2 py-0.5 rounded-full border ${ev.classes}`}
                          >
                            <Evi size={11} /> {t(ev.labelKey)}
                          </span>
                        );
                      })()}
                    </td>
                    <td className="p-3">
                      <span
                        className={`text-xs inline-flex items-center gap-1 px-2 py-0.5 rounded-full border bg-${ch.color}-50 text-${ch.color}-700 border-${ch.color}-200`}
                      >
                        <Icon size={11} /> {t(ch.labelKey)}
                      </span>
                    </td>
                    <td className="p-3 text-xs text-slate-500">
                      {n.sent_by_name || "—"}
                    </td>
                    <td className="p-3 text-xs text-slate-700 max-w-md truncate">
                      {n.message}
                    </td>
                    <td className="p-3 text-right">
                      <button
                        onClick={() => setDeleteId(n.id)}
                        data-testid={`notif-delete-${n.id}`}
                        className="w-8 h-8 inline-flex items-center justify-center rounded-lg border border-red-200 bg-red-50 text-red-700 hover:bg-red-100 transition-colors"
                        title={t("Excluir")}
                      >
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Delete one */}
      <Dialog
        open={!!deleteId}
        onOpenChange={(v) => {
          if (!v) {
            setDeleteId(null);
            setPassword("");
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lock size={18} className="text-red-600" />
              {t("Excluir notificação")}
            </DialogTitle>
            <DialogDescription>
              {t("Digite sua senha de admin para confirmar a exclusão.")}
            </DialogDescription>
          </DialogHeader>
          <input
            type="password"
            className="cm-input"
            placeholder={t("Senha do admin")}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            data-testid="delete-password-input"
            autoFocus
          />
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setDeleteId(null);
                setPassword("");
              }}
            >
              {t("Cancelar")}
            </Button>
            <Button
              onClick={deleteOne}
              disabled={!password || busy}
              data-testid="confirm-delete-btn"
              className="bg-red-600 hover:bg-red-700"
            >
              {t("Confirmar exclusão")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Clear all */}
      <Dialog
        open={confirmClearOpen}
        onOpenChange={(v) => {
          if (!v) {
            setConfirmClearOpen(false);
            setPassword("");
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <Lock size={18} />
              {t("Apagar TODO o histórico")}
            </DialogTitle>
            <DialogDescription>
              {t("Esta ação é irreversível. Todos os {count} registros serão removidos. Digite sua senha de admin para confirmar.").replace("{count}", items.length)}
            </DialogDescription>
          </DialogHeader>
          <input
            type="password"
            className="cm-input"
            placeholder={t("Senha do admin")}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            data-testid="clear-password-input"
            autoFocus
          />
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setConfirmClearOpen(false);
                setPassword("");
              }}
            >
              {t("Cancelar")}
            </Button>
            <Button
              onClick={clearAll}
              disabled={!password || busy}
              data-testid="confirm-clear-btn"
              className="bg-red-600 hover:bg-red-700"
            >
              {t("Apagar tudo")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
